import { isPlatformBrowser } from '@angular/common';
import { Component, Inject, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AlertContainerComponent } from './shared/views/components/alert-container/alert-container.component';
import { ModalLoaderComponent } from './shared/views/components/modals/modal-loader/modal-loader.component';
import { DevToolsPanelComponent } from './core/dev-tools/dev-tools-panel.component';
import { runDetailedKeycloakDiagnostic } from './keycloak-diagnostic';
import { ThemeService } from './shared/services/theme/theme.service';
import { UserChangeDetectorService } from './core/services/auth/user-change-detector.service';
import { GuestSessionPurgeService } from './core/services/auth/guest-session-purge.service';
import { AlertService } from './shared/services/alert/alert.service';
import { GuidedTourComponent } from './shared/views/components/guided-tour/guided-tour.component';

@Component({
  selector: 'app-root',
  imports: [RouterOutlet, AlertContainerComponent, ModalLoaderComponent, TranslateModule, DevToolsPanelComponent, GuidedTourComponent],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent {
  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object,
    private translate: TranslateService,
    private meta: Meta,
    private title: Title,
    private themeService: ThemeService,
    private userChangeDetector: UserChangeDetectorService,
    private guestSessionPurge: GuestSessionPurgeService,
    private alertService: AlertService) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && isPlatformBrowser(this.platformId)) {
        // Le scroll smooth est géré par GuestLayoutComponent via GSAP
      }
    });
  }

  currentLang: string = 'fr';

  ngOnInit(): void {
    // 🔍 Diagnostic Keycloak détaillé
    if (isPlatformBrowser(this.platformId)) {
      runDetailedKeycloakDiagnostic();
      this.userChangeDetector.start();
      this.notifyIfGuestSessionExpired();

      // Sécurité : bloquer les appels à `alert()` provenant souvent des scripts Form.io (Custom Logic)
      window.alert = () => {
        /* no-op : alerte native bloquée */
      };
    }

    this.currentLang = this.translate.currentLang || this.translate.getDefaultLang() || 'fr';
    this.translate.onLangChange.subscribe(event => {
      this.currentLang = event.lang;
      this.updateSEOMetadata();
    });

    this.updateSEOMetadata();
  }

  /**
   * Explique le retour forcé sur l'accueil lorsqu'une session invitée morte
   * vient d'être purgée (cf. `GuestSessionPurgeService.purgeAndHardRefresh`).
   *
   * Le marqueur est consommé ici, une fois pour toutes. `get()` et non
   * `instant()` : au tout premier démarrage le bundle de traduction est encore
   * en vol, et `instant()` afficherait la clé brute.
   */
  private notifyIfGuestSessionExpired(): void {
    if (!this.guestSessionPurge.consumeExpiredNotice()) return;

    this.translate.get([
      'CONSULT.SESSION_EXPIREE_TITRE',
      'PAYMENT.SESSION_PURGEE_MESSAGE'
    ]).subscribe(translations => {
      this.alertService.warning(
        translations['CONSULT.SESSION_EXPIREE_TITRE'],
        translations['PAYMENT.SESSION_PURGEE_MESSAGE'],
        10
      );
    });
  }

  updateSEOMetadata(): void {
    this.translate.get([
      'SEO.TITLE',
      'SEO.DESCRIPTION',
      'SEO.KEYWORDS',
      'SEO.OG_TITLE',
      'SEO.OG_DESCRIPTION'
    ]).subscribe(translations => {
      this.title.setTitle(translations['SEO.TITLE']);
      
      // Meta tags standards
      this.meta.updateTag({ name: 'description', content: translations['SEO.DESCRIPTION'] });
      this.meta.updateTag({ name: 'keywords', content: translations['SEO.KEYWORDS'] });
      this.meta.updateTag({ name: 'author', content: "Ministère de l'Économie Numérique et de la Transformation Digitale du Togo" });
      this.meta.updateTag({ name: 'robots', content: 'index, follow' });
      this.meta.updateTag({ name: 'theme-color', content: '#1879C2' });

      // Open Graph
      this.meta.updateTag({ property: 'og:title', content: translations['SEO.OG_TITLE'] });
      this.meta.updateTag({ property: 'og:description', content: translations['SEO.OG_DESCRIPTION'] });
      this.meta.updateTag({ property: 'og:type', content: 'website' });
      this.meta.updateTag({ property: 'og:url', content: 'https://service-public.gouv.tg/' });
      this.meta.updateTag({ property: 'og:image', content: 'service-public-logo.png' });
      this.meta.updateTag({ property: 'og:image:alt', content: 'GNDAD Togo Logo' });
      this.meta.updateTag({ property: 'og:locale', content: this.currentLang === 'en' ? 'en_US' : 'fr_TG' });
      this.meta.updateTag({ property: 'og:site_name', content: 'GNDAD Togo' });

      // Twitter Cards
      this.meta.updateTag({ name: 'twitter:card', content: 'summary_large_image' });
      this.meta.updateTag({ name: 'twitter:title', content: translations['SEO.OG_TITLE'] });
      this.meta.updateTag({ name: 'twitter:description', content: translations['SEO.OG_DESCRIPTION'] });
      this.meta.updateTag({ name: 'twitter:image', content: 'service-public-logo.png' });
    });
  }
}
